import React from 'react';

import { Context } from 'internal/Provider';

import { eventHasKey, KEY } from 'utilsTS/dom';
import { useBrowser } from 'utilsTS/hooks';

export function useClickOutside<T extends HTMLElement>(
    handler: (event: MouseEvent | KeyboardEvent, isClick?: boolean) => void,
    isActive?: boolean,
    triggerOnClick = true,
    triggerOnEsc = true,
): React.RefObject<T> {
    const ref = React.useRef<T>(null!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    const { document } = useBrowser(React.useContext(Context));

    React.useEffect(() => {
        if (!isActive || !document) return () => {};

        const handleClick = (event: MouseEvent) => {
            if (!triggerOnClick) return;
            if (!ref.current || ref.current.contains(event.target as Node)) return;
            handler(event, true);
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (!triggerOnEsc) return;
            if (!eventHasKey(event, KEY.ESC)) return;
            handler(event, false);
        };

        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handler, isActive, triggerOnClick, triggerOnEsc, document]);

    return ref;
}
